import {
    ExamStats,
    ExamTime,
    FirestoreUser,
    getDefaultExamStats,
    getDefaultExamStatsForKey,
    QuizQuestion
} from './interface';
import {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {firestore} from 'firebase/app';
import {useCurrentUserId, useIsUserLoggedIn} from './auth';
import {randomizeQuestions, uniqueArray} from "../helpers/helper-functions";
// @ts-ignore
import isEqual from 'lodash/isEqual';
import {signOut} from "../actions/auth";

// Globally store Exam Questions to avoid excessive firestore calls
let listeners: Dispatch<SetStateAction<FirestoreUser>>[] = [];
let firestoreUser: FirestoreUser = {
    uid: '',
    answerValues: undefined,
    answeredQuestions: undefined,
    availableExam: '',
    email: '',
    examTime: {},
    firstname: '',
    flaggedQuestions: undefined,
    lastname: '',
    licenseExpirationDate: '',
    practiceAnsweredQuestions: [],
    simulationAnsweredQuestions: [],
    examStats: {},
};
let cancelSubscription: (() => unknown) | undefined = undefined;


let userCache: Record<string, FirestoreUser> = {}

const setFirestoreUser = (newState: FirestoreUser) => {
    firestoreUser = newState;
    listeners.forEach((listener) => {
        listener(firestoreUser);
    });
};

export function useSelectUserProperty<K extends keyof FirestoreUser>(key: K): FirestoreUser[K] {
    const firestoreUser = useCurrentFirestoreUser();
    const [prop, setProp] = useState<FirestoreUser[K]>()
    useEffect(() => {
        const newProp = firestoreUser[key];
        if (!isEqual(prop, newProp)) {
            setProp(firestoreUser[key]);
        }
    }, [firestoreUser, key, prop]);
    return prop!;
}

export function useSelectExamStats<Q extends Pick<QuizQuestion, 'id' | 'showAfter' | 'userDefinedId'>>(examKey: string, questions: Q[]): ExamStats {
    const uid = useSelectUserProperty('uid');
    // Use getDefaultExamStatsForKey for a cached object to prevent duplicate re-renders
    let stats = useSelectUserProperty('examStats')?.[examKey]
    if (!stats || Object.keys(stats).length === 0) {
        stats = getDefaultExamStatsForKey(uid, examKey);
    }
    if (!stats.currentQuestionOrder?.length) {
        const questionOrder = randomizeQuestions(questions || []);
        stats.currentQuestionOrder = uniqueArray(questionOrder.map(q => q.id));
    }
    // If there are questions that haven't been accounted for yet, push them onto the end.
    (questions || []).forEach(q => {
        if (stats.currentQuestionOrder.indexOf(q.id) < 0) {
            stats.currentQuestionOrder.push(q.id);
        }
    });
    if (!stats.currentQuestionId) {
        stats.currentQuestionId = stats.currentQuestionOrder[0];
    }
    return stats;
}

export async function getUserExamTime(uid: string, examKey: string): Promise<Omit<ExamTime, 'timerActive'>> {
    const user = await getFirestoreUser(uid, true);
    if (!user.examTime) {
        user.examTime = {};
    }
    if (!user.examTime?.[examKey]) {
        user.examTime[examKey] = {hours: 0, minutes: 0, seconds: 0}
    }
    return user.examTime[examKey];
}

export function saveUserExamTime(uid: string, examKey: string, examTime: ExamTime) {
    firestore().collection('User').doc(uid).set({examTime: {[examKey]: examTime}}, {merge: true})
}

export async function waitForExamStatsToLoad(uid: string): Promise<void> {
    await getFirestoreUser(uid);
}

export function useCurrentFirestoreUser(): FirestoreUser {
    const isLoggedIn = useIsUserLoggedIn();
    const uid = useCurrentUserId();
    const newListener = useState(firestoreUser)[1];

    useEffect(() => {
        listeners.push(newListener);
    }, [newListener]);

    useEffect(() => {
        // Already listening
        if (isLoggedIn && cancelSubscription) {
            return;
        }
        // Logged out, and is not listening
        if (!isLoggedIn && !cancelSubscription) {
            return;
        }
        // Logged in, not listening yet
        if (isLoggedIn && uid && !cancelSubscription) {
            cancelSubscription = firestore()
                .collection('User')
                .doc(uid)
                .onSnapshot({
                    next: (doc: firestore.DocumentSnapshot) => {
                        const u = doc.data() as FirestoreUser;
                        const firestoreUser = {...u, uid: u.uid || uid, examStats: u.examStats || getDefaultExamStats()};
                        setFirestoreUser(firestoreUser);
                        userCache[u.uid] = firestoreUser
                    },
                    error: (error: firestore.FirestoreError) => {
                        if (error.code === 'permission-denied') {
                            console.error(`reloading due to permission-denied error: ${error}`);
                            signOut().then(() =>  window.location.reload());
                        }
                        console.error(`got firestore error reading firestoreUser: ${error}`);
                    }
                })
        }
        return () => {
            listeners = listeners.filter(listener => listener !== newListener);
            // only unsubscribe if we are logged out. Keeping the subscription alive does not have bad consequences
            if (!isLoggedIn && cancelSubscription) {
                cancelSubscription();
                cancelSubscription = undefined;
            }
        }
    }, [isLoggedIn, newListener, uid]);
    return firestoreUser;
}

export async function getFirestoreUser(uid: string, useCache?: boolean): Promise<FirestoreUser> {
    if (useCache && userCache[uid]) {
        return userCache[uid];
    }
    return firestore()
        .collection('User')
        .doc(uid)
        .get()
        .then(doc => {
            const u = doc.data() as FirestoreUser;
            const firestoreUser = {...u, uid: u.uid || uid, examStats: u.examStats || getDefaultExamStats()};
            userCache[u.uid] = firestoreUser;
            return firestoreUser;
        })
}
